/**
 * SifreBelirle — herkese açık şifre belirleme sayfası (backlog #556, backend #555). Rota: /sifre-belirle?anahtar=<anahtar>.
 *
 * Eski PHP'nin `/Login/emailactivation/<anahtar>` sayfasının karşılığı. Oturum istemez (publicRoutes). Açılışta
 * `GET /public/sifre?anahtar=` kimin şifresinin belirleneceğini söyler; form yeni şifre + tekrarı alır (tekrar yalnızca burada
 * denetlenir), `POST /public/sifre` şifreyi yazar ve bağlantıyı kullanılmış sayar. Sonra Giriş'e giden bir düğme çıkar.
 *
 * Bağlantı geçersiz (404: bilinmiyor ya da kullanılmış) ya da süresi dolmuşsa (410) form çıkmaz, yeni bağlantı istenmesi söylenir.
 * Kısa şifrede (400) bağlantı çalışmaya devam eder, form yerinde kalır. Backend metinleri istekteki dilde döner, ama sayfanın kendi
 * TR / EN anahtarı olduğundan hata, `errorKey`'e göre sayfanın t() metninden çizilir (dil değişince hemen güncellenir); bilinmeyen
 * bir anahtar gelirse backend'in kendi metni gösterilir. Anahtar adres çubuğundan başka yere yazılmaz, konsola basılmaz.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, CardBody, Col, Container, Form, Input, Label, Row, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { LanguageContext } from '../../../Components/Contexts/LanguageContext';
import LanguageDropdown from '../../../Components/Common/LanguageDropdown';
import logoLight from '../../../assets/images/logo-light.png';
import { STORAGE_KEY, currentLocaleTag } from '../../../i18n';
import { EN_AZ_SIFRE, getSifreLinkiBilgisi, sifreBelirle } from '../../../services/sifreBelirleService';

// Backend Mesaj anahtarı → sayfanın giris.sifreBelirle.* anahtarı.
const HATA_ANAHTARLARI = {
  'sifre.linki_gecersiz': 'sifreBelirle.linkGecersiz',
  'sifre.linki_suresi_doldu': 'sifreBelirle.linkSuresiDoldu',
  'sifre.linki_sifre_kisa': 'sifreBelirle.sifreKisa',
};

function tarihSaat(v) {
  if (!v) return null;
  const d = new Date(String(v).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString(currentLocaleTag(), { dateStyle: 'medium', timeStyle: 'short' });
}

const SifreBelirle = () => {
  const { t } = useTranslation('giris');
  const [searchParams] = useSearchParams();
  const anahtar = (searchParams.get('anahtar') || '').trim();

  // Bu sayfada başlık çubuğu yok: kendi TR / EN anahtarı var. Hiçbir dil seçilmemişse ilk açılışta tarayıcının dili (Türkçe değilse
  // İngilizce), sonra kullanıcının kendi seçimi hatırlanır (tadilat formuyla aynı).
  const { selectedLang, setSelectedLang } = useContext(LanguageContext);
  useEffect(() => {
    let stored = null;
    try { stored = localStorage.getItem(STORAGE_KEY); } catch { /* özel pencere */ }
    if (!stored) setSelectedLang(/^tr\b/i.test(navigator.language || '') ? 'tr' : 'en');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { document.title = t('sifreBelirle.sayfaBasligi'); }, [t]);

  const [bilgi, setBilgi] = useState(null);       // { kullaniciAdi, gecerlilikBitis }
  const [yukleniyor, setYukleniyor] = useState(Boolean(anahtar));
  const [linkHatasi, setLinkHatasi] = useState(anahtar ? null : { errorKey: null, mesaj: null, status: null }); // form gösterilemeyen hata
  const [sifre, setSifre] = useState('');
  const [tekrar, setTekrar] = useState('');
  const [gor, setGor] = useState(false);
  const [gonderiyor, setGonderiyor] = useState(false);
  const [formHatasi, setFormHatasi] = useState(null); // { errorKey, mesaj } | { yerel: anahtar }
  const [bitti, setBitti] = useState(null);           // POST yanıtı

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!anahtar) return undefined;
    let ignore = false;
    async function run() {
      try {
        const d = await getSifreLinkiBilgisi(anahtar);
        if (!ignore) setBilgi(d);
      } catch (e) {
        if (!ignore) setLinkHatasi({ errorKey: e.errorKey, mesaj: e.status ? e.message : null, status: e.status });
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [anahtar]);

  function hatayiYaz(h) {
    if (!h) return null;
    if (h.yerel) return t(h.yerel, { sayi: EN_AZ_SIFRE });
    const yerelAnahtar = HATA_ANAHTARLARI[h.errorKey];
    if (yerelAnahtar) return t(yerelAnahtar, { sayi: EN_AZ_SIFRE });
    return h.mesaj || t('sifreBelirle.baglantiHatasi');
  }

  async function kaydet(e) {
    e.preventDefault();
    if (sifre.length < EN_AZ_SIFRE) { setFormHatasi({ yerel: 'sifreBelirle.sifreKisa' }); return; }
    if (sifre !== tekrar) { setFormHatasi({ yerel: 'sifreBelirle.sifreUyusmuyor' }); return; }
    setFormHatasi(null);
    setGonderiyor(true);
    try {
      setBitti(await sifreBelirle(anahtar, sifre));
      setSifre('');
      setTekrar('');
    } catch (err) {
      if (err.status === 404 || err.status === 410) {
        // Bağlantı bu arada kullanıldı ya da süresi doldu: form gösterecek bir şey kalmadı.
        setLinkHatasi({ errorKey: err.errorKey, mesaj: err.message, status: err.status });
      } else {
        setFormHatasi({ errorKey: err.errorKey, mesaj: err.status ? err.message : null });
      }
    } finally {
      setGonderiyor(false);
    }
  }

  let icerik;
  if (yukleniyor) {
    icerik = (
      <div className="text-center py-4">
        <Spinner color="primary" />
        <p className="mt-2 text-muted">{t('common:yukleniyor')}</p>
      </div>
    );
  } else if (bitti) {
    icerik = (
      <div className="text-center mt-2">
        <h5 className="text-body">{t('sifreBelirle.sifreKaydedildi')}</h5>
        <p className="text-muted">{bitti.mesaj || t('sifreBelirle.sifreKaydedildiAciklama')}</p>
        <Link to="/Login" className="btn btn-primary w-100 mt-2">{t('homeLogin.girisYap')}</Link>
      </div>
    );
  } else if (linkHatasi) {
    icerik = (
      <div className="text-center mt-2">
        <h5 className="text-body">{t('sifreBelirle.linkKullanilamiyor')}</h5>
        <Alert color="danger" fade={false} className="mt-3 mb-2">
          {anahtar ? hatayiYaz(linkHatasi) : t('sifreBelirle.linkEksik')}
        </Alert>
        {/* Sunucuya hiç ulaşılamadıysa (status yok) bağlantı suçlu değil: yeni bağlantı istemek bir şey çözmez. */}
        {(!anahtar || linkHatasi.status) && <p className="text-muted small mb-0">{t('sifreBelirle.yeniBaglantiIste')}</p>}
      </div>
    );
  } else {
    icerik = (
      <>
        <div className="text-center mt-2">
          <h5 className="text-body">{t('sifreBelirle.baslik')}</h5>
          <p className="text-muted mb-1">{t('sifreBelirle.aciklama')}</p>
          <p className="mb-0">
            <span className="text-muted">{t('homeLogin.kullaniciAdi')}: </span>
            <span className="fw-semibold font-monospace">{bilgi?.kullaniciAdi}</span>
          </p>
          {bilgi?.gecerlilikBitis && (
            <p className="text-muted small mt-1 mb-0">{t('sifreBelirle.gecerlilik', { tarih: tarihSaat(bilgi.gecerlilikBitis) })}</p>
          )}
        </div>
        {formHatasi && <Alert color="danger" fade={false} className="mt-3 mb-0">{hatayiYaz(formHatasi)}</Alert>}
        <div className="p-2">
          <Form onSubmit={kaydet}>
            <div className="mb-3">
              <Label htmlFor="sifre-yeni" className="form-label">{t('sifreBelirle.yeniSifre')}</Label>
              <div className="position-relative auth-pass-inputgroup">
                <Input
                  id="sifre-yeni" name="sifre" type={gor ? 'text' : 'password'} className="form-control pe-5" autoComplete="new-password"
                  placeholder={t('sifreBelirle.enAz', { sayi: EN_AZ_SIFRE })} value={sifre}
                  onChange={(e) => setSifre(e.target.value)} invalid={Boolean(formHatasi)}
                />
                <button
                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted" type="button"
                  title={gor ? t('sifreBelirle.gizle') : t('sifreBelirle.goster')} onClick={() => setGor(!gor)}
                >
                  <i className={gor ? 'ri-eye-off-fill align-middle' : 'ri-eye-fill align-middle'}></i>
                </button>
              </div>
            </div>
            <div className="mb-3">
              <Label htmlFor="sifre-tekrar" className="form-label">{t('sifreBelirle.sifreTekrar')}</Label>
              <Input
                id="sifre-tekrar" name="sifreTekrar" type={gor ? 'text' : 'password'} className="form-control" autoComplete="new-password"
                value={tekrar} onChange={(e) => setTekrar(e.target.value)} invalid={Boolean(formHatasi)}
              />
            </div>
            <Button color="primary" className="w-100" type="submit" disabled={gonderiyor || !sifre || !tekrar}>
              {gonderiyor ? <Spinner size="sm" /> : t('sifreBelirle.kaydet')}
            </Button>
          </Form>
        </div>
      </>
    );
  }

  return (
    <div className="auth-page-wrapper pt-5">
      <div className="auth-one-bg-position auth-one-bg" id="auth-particles">
        <div className="bg-overlay"></div>
        <div className="shape">
          <svg xmlns="http://www.w3.org/2000/svg" version="1.1" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 1440 120">
            <path d="M 0,36 C 144,53.6 432,123.2 720,124 C 1008,124.8 1296,56.8 1440,40L1440 140L0 140z"></path>
          </svg>
        </div>

        <div className="position-absolute top-0 end-0 p-3" style={{ zIndex: 10 }}>
          <LanguageDropdown selectedLang={selectedLang} setSelectedLang={setSelectedLang} />
        </div>

        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <img src={logoLight} alt="" height="100" />
                </div>
              </Col>
            </Row>
            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4 card-bg-fill">
                  <CardBody className="p-4">{icerik}</CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </div>
  );
};

export default SifreBelirle;
